/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
import { BehaviorSubject, Observable, Subscription, combineLatest } from "rxjs";
import { distinctUntilChanged, map, tap } from "rxjs/operators";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import {
    FormField,
    FormManagerErrors,
    FormManagerOptions,
    FormManagerValues,
    UpdateValueOptions,
    ValidationStrategy,
} from "./types";

export default class FormManager {
    private valuesSubscription: Subscription | undefined;
    private errorsSubscription: Subscription;
    private validationStrategy$ = new BehaviorSubject(
        ValidationStrategy.OnSubmit
    );
    public fields$ = new BehaviorSubject<FormField[]>([]);
    public values$ = new BehaviorSubject<FormManagerValues>({});
    public errors$ = new BehaviorSubject<FormManagerErrors>({});
    public errorsCount$: Observable<number>;
    public touchedFields$ = new BehaviorSubject<string[]>([]);

    constructor(
        private options: FormManagerOptions,
        private logger?: LoggerInterface
    ) {
        logger?.debug("new FormManager", {
            options,
        });

        this.values$.next({ ...options.initialValues });

        this.valuesSubscription = combineLatest([
            this.validationStrategy$,
            this.values$,
        ])
            .pipe(
                map(([validationStrategy, values]) => {
                    let validate = false;
                    if (validationStrategy === ValidationStrategy.OnChange) {
                        validate = true;
                    }

                    return [validate, values];
                })
            )
            .subscribe(([validate, values]) => {
                if (validate) {
                    void this.validate(values as FormManagerValues);
                }
            });

        this.validationStrategy$.next(options.validationStrategy);

        this.errorsSubscription = this.errors$.subscribe((errors) => {
            this.logger?.debug("FormManager.constructor, errors:", {
                errors,
            });
        });

        this.errorsCount$ = this.errors$.pipe(
            map((errors) => Object.keys(errors).length)
        );
    }

    register(field: FormField, defaultValue?: unknown) {
        this.logger?.debug("FormManager.register", { field });
        const fields = this.fields$.getValue();
        const isFieldExist = fields.find(
            (formField) => formField.name === field.name
        );

        if (isFieldExist) {
            throw new Error(`Field with name ${field.name} already exists`);
        }

        fields.push(field);
        this.fields$.next(fields);

        if (defaultValue !== undefined && defaultValue !== null) {
            const values = this.values$.getValue();
            const isValueExist = values[field.name] !== undefined;

            if (!isValueExist) {
                values[field.name] = defaultValue;
                this.values$.next(values);
            } else {
                console.warn(
                    `Default value for ${field.name} already passed from initial values`
                );
            }
        }

        return () => this.unregister(field.name);
    }

    unregister(name: string) {
        const fields = this.fields$.getValue();
        const fieldIdx = fields.findIndex((field) => field.name === name);

        if (fieldIdx > -1) {
            fields.splice(fieldIdx, 1);
            this.fields$.next(fields);

            if (this.options.unregisterOnUnmount) {
                const values = this.values$.getValue();
                delete values[name];
                this.values$.next(values);
            }
        }

        const touchedFields = this.touchedFields$.getValue();
        const touchedFieldIdx = touchedFields.indexOf(name);

        if (touchedFieldIdx > -1) {
            touchedFields.splice(touchedFieldIdx);
            this.touchedFields$.next(touchedFields);
        }
    }

    selectValue(name: string) {
        this.logger?.debug("FormManager.selectValue", { name });
        return this.values$.pipe(
            map((v) => v[name]),
            distinctUntilChanged(),
            tap((value) => {
                this.logger?.debug("FormManager.selectValue, new value", {
                    name,
                    value,
                });
            })
        );
    }

    selectValues() {
        return this.values$;
    }

    selectError(name: string) {
        this.logger?.debug("FormManager.selectError", { name });
        return this.errors$.pipe(
            map((v) => v[name]),
            distinctUntilChanged(),
            tap((value) => {
                this.logger?.debug("FormManager.selectError", {
                    name,
                    value,
                });
            })
        );
    }

    updateValue(
        name: string,
        value: unknown,
        options: UpdateValueOptions = { shouldTouch: true }
    ) {
        const values = this.values$.getValue();

        values[name] = value;

        this.values$.next(values);

        const touchedFields = this.touchedFields$.getValue();
        const touchedFieldIdx = touchedFields.indexOf(name);

        if (options?.shouldTouch && touchedFieldIdx === -1) {
            touchedFields.push(name);
            this.touchedFields$.next(touchedFields);
        }
    }

    resetValues(values: FormManagerValues) {
        this.logger?.debug("FormManager.resetValues", { values });
        this.values$.next(values);
        this.touchedFields$.next([]);
    }

    async validate(values: FormManagerValues, validateAll = false) {
        const touchedFields = this.touchedFields$.getValue();
        const result = await this.options.resolver(values);

        if (!result.errors) {
            this.errors$.next({});
            return result;
        }

        if (validateAll) {
            this.errors$.next(result.errors);
            return result;
        }

        const errorsFromTouchedFields = Object.fromEntries(
            Object.entries(result.errors).filter(([field]) =>
                touchedFields.includes(field)
            )
        );
        this.errors$.next(errorsFromTouchedFields);

        return {
            data: null,
            errors: errorsFromTouchedFields,
        };
    }

    submit = async () => {
        const values = this.values$.getValue();
        const fields = this.fields$.getValue();

        this.touchedFields$.next(fields.map((field) => field.name));

        const result = await this.validate(values, true);
        if (this.options.onSubmit) {
            void this.options.onSubmit({
                values: result.data,
                errors: result.errors,
            });
        }

        if (result.errors) {
            this.validationStrategy$.next(ValidationStrategy.OnChange);
        } else {
            this.validationStrategy$.next(this.options.validationStrategy);
        }
    };

    destroy = () => {
        this.valuesSubscription?.unsubscribe();
        this.errorsSubscription.unsubscribe();
    };

    updateOptions(
        newOptions:
            | FormManagerOptions
            | ((state: FormManagerOptions) => FormManagerOptions)
    ) {
        if (typeof newOptions === "function") {
            this.options = newOptions(this.options);
            return;
        }

        this.options = newOptions;
    }
}
